import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation, Trans } from 'react-i18next';
import { WeezHeader, Card, Button, Select, Section, Message, RadioLabel, Input, Form, Date, ToastContext, TooltipHelp } from '@weezevent/nacre';
import { useForm } from '../../../utils/validators';
import {
    DigitickForm,
    FeverForm,
    FnacForm,
    UniverseForm,
    LenndForm,
    TicketnetForm,
    PlaypassForm,
    SecutixForm,
    SeeticketsDEForm,
    SeeticketESForm,
    RegiondoForm,
    VivenuForm
} from './webserviceForms';
import i18n from 'i18next';
import moment from 'moment';
import useAppContext from '../../../context/useAppContext';
import SubscriptionModel from '../../../models/subscription';
import { DateTimeInput } from '../../../components/date-time-input/dateTimeInput';
import { useHistory } from 'react-router-dom';
import Styles from './createWebservice.module.css';
import { usePermissions } from '../../../user/permissions';

const WEBSERVICE_FORMS = {
    digitick: DigitickForm,
    fever: FeverForm,
    fnac: FnacForm,
    universe: UniverseForm,
    lennd: LenndForm,
    ticketnet: TicketnetForm,
    playpass: PlaypassForm,
    secutix: SecutixForm,
    seetickets_de: SeeticketsDEForm,
    seetickets_es: SeeticketESForm,
    regiondo: RegiondoForm,
    vivenu: VivenuForm
};

function TypeComponent({ onCardDone, formState, setFormState, disableHook }) {
    const { t } = useTranslation();

    return (
        <div>
            <div>{t('ticket.webservice.create.type-label')}</div>
            <div>
                <RadioLabel
                    inline
                    bordered
                    name="ticket-provider"
                    checked={formState.type !== 'hook'}
                    label={t('ticket.webservice.create.option-ticket-provider')}
                    onChange={() => setFormState('type', null)}
                />
                <RadioLabel
                    inline
                    bordered
                    name="custom-import"
                    checked={formState.type === 'hook'}
                    label={t('ticket.webservice.create.option-custom-import')}
                    onChange={() => setFormState('type', 'hook')}
                    disabled={disableHook}
                />
            </div>
            <Button primary onClick={() => onCardDone()}>
                {t('ticket.webservice.create.continue')}
            </Button>
        </div>
    );
}

TypeComponent.propTypes = {
    onCardDone: PropTypes.func,
    formState: PropTypes.object,
    setFormState: PropTypes.func.isRequired
};

function SelectDistributors({ formState, setFormState, formErrors, setFormStateObject, onSubmit, isSubmitting, setFormErrors, initialValues }) {
    const { t } = useTranslation();

    // render corresponding form depending on selected distributor type
    const renderFields = React.useCallback(() => {
        let Component = WEBSERVICE_FORMS[formState.type];
        return <Component formState={formState} setFormState={setFormState} formErrors={formErrors} />;
    }, [formState, formErrors]);

    const isDisabled = React.useMemo(() => {
        return isSubmitting || !formState.type || Object.keys(formErrors).length > 0;
    }, [isSubmitting, formState, formErrors]);

    const onChange = React.useCallback(
        (_, { value }) => {
            if (formState.type !== value) {
                setFormStateObject({ ...initialValues, type: value }, true);
                return setFormErrors({});
            }
        },
        [formState, setFormStateObject, initialValues]
    );

    const options = [
        { text: t('ticket.webservice.form.choiceDistributor'), value: '' },
        { text: 'Digitick', value: 'digitick' },
        { text: 'Fever', value: 'fever' },
        { text: 'Fnac', value: 'fnac' },
        { text: 'Universe', value: 'universe' },
        { text: 'Lennd', value: 'lennd' },
        { text: 'Ticketnet', value: 'ticketnet' },
        { text: 'Playpass', value: 'playpass' },
        { text: 'Secutix', value: 'secutix' },
        { text: 'Seetickets DE', value: 'seetickets_de' },
        { text: 'Seetickets ES', value: 'seetickets_es' },
        { text: 'Regiondo', value: 'regiondo' },
        { text: 'Vivenu', value: 'vivenu' }
    ];

    return (
        <div>
            <div>{t('ticket.webservice.create.information-label')}</div>
            <Select className={Styles['distributors-select']} options={options} value={formState.type || ''} onChange={onChange} />
            {formState.type && <Section>{renderFields()}</Section>}
            <Button primary disabled={isDisabled} onClick={onSubmit}>
                {t('ticket.common.cta.save')}
            </Button>
        </div>
    );
}

SelectDistributors.propTypes = {
    formState: PropTypes.object,
    setFormState: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    setFormStateObject: PropTypes.func.isRequired,
    formErrors: PropTypes.object,
    isSubmitting: PropTypes.bool,
    setFormErrors: PropTypes.func,
    initialValues: PropTypes.object
};

function CreateImport({ formState, setFormState, timezone, onSubmit, isSubmitting, formErrors }) {
    const { t } = useTranslation();
    const locale = i18n.language;
    const currentYear = new Date().getFullYear();

    // form error if selected date is in the past
    const warningPastDate = React.useMemo(() => {
        const now = moment()
            .tz(timezone)
            .format();
        if (formState.end_date && formState.end_date < now) {
            return t('ticket.webservice.create.import.date-error');
        }
        return false;
    }, [timezone, formState, t]);

    const options_provider = [
        { text: 'Default', value: 'default' },
        { text: 'Seetickets', value: 'seetickets' },
        { text: 'Ticketmaster', value: 'ticketmaster' },
        { text: 'Eventim', value: 'eventim' },
        { text: 'Eventbrite', value: 'eventbrite' },
        { text: 'Paylogic', value: 'paylogic' }
    ];

    const optionsCreateForm = [
        { text: t('ticket.webservice.form.createFormBy.rate'), value: 'rate' },
        { text: t('ticket.webservice.form.createFormBy.management_rule'), value: 'subscription_link' },
        { text: t('ticket.webservice.form.createFormBy.event'), value: 'event' }
    ];

    const onChangeProvider = React.useCallback(
        (_, { value }) => {
            setFormState('default_provider', value);
        },
        [formState, setFormState]
    );

    return (
        <div>
            <div className={Styles['hook-name']}>{t('ticket.webservice.create.import.name')}</div>
            <Form.Field>
                <Input onChange={({ target: { value } }) => setFormState('name', value)} />
            </Form.Field>
            <Form.FieldErrors errors={[t(formErrors['name']) || ['']]} />
            <div className={Styles['modal-settings-event-section']}>
                <div className={Styles['date-container']}>
                    <Form.Field>
                        <DateTimeInput
                            className={Styles['date-time-input']}
                            timeLabel={
                                <>
                                    {t('ticket.common.time.end')}
                                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                                </>
                            }
                            dateLabel={
                                <>
                                    {t('ticket.common.date.end')}
                                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                                </>
                            }
                            onChange={date => setFormState('end_date', date)}
                            value={formState.end_date}
                            dateProps={{ minYear: currentYear, minDate: moment() }}
                            locale={locale}
                            timezone={timezone}
                        />
                    </Form.Field>
                    <div className={Styles['timezone-container']}>
                        <div>{timezone}</div>
                    </div>
                </div>
                {Boolean(warningPastDate) && <Form.FieldErrors errors={[warningPastDate]} />}
            </div>
            <div>
                <Form.Label>
                    {t('ticket.webservice.form.choiceProviderTitle')}
                    <TooltipHelp title={t('ticket.webservice.form.choiceProviderTooltip')} className={Styles['provider-option-select']} />
                    <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                </Form.Label>
                <Select options={options_provider} placeholder={t('ticket.webservice.form.choiceProvider')} onChange={onChangeProvider} className={Styles['provider-select']} />
                <Form.Label>{t('ticket.webservice.form.createFormBy.label')}</Form.Label>
                <Select
                    options={optionsCreateForm}
                    placeholder={t('ticket.webservice.form.choiceProvider')}
                    onChange={(_, { value }) => setFormState('create_form_by', value)}
                    className={Styles['provider-select']}
                    value={formState.create_form_by}
                />
            </div>
            <Button primary disabled={isSubmitting || !formState.name} onClick={onSubmit}>
                {t('ticket.webservice.create.import.cta')}
            </Button>
        </div>
    );
}

CreateImport.propTypes = {
    formState: PropTypes.object,
    setFormState: PropTypes.func.isRequired,
    timezone: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool,
    formErrors: PropTypes.object
};

export default function CreateWebservice({ organizationId }) {
    const { t } = useTranslation();
    const history = useHistory();

    let [{ organization }] = useAppContext();
    const toast = React.useContext(ToastContext);

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const permissions = usePermissions();

    const initialValues = {
        type: null,
        name: '',
        end_date: null,
        channel_id: null,
        organizer_id: '',
        login: '',
        password: '',
        account_type: null,
        wsdl: null,
        tenant: '',
        getin_url: null,
        url: '',
        api_key: '',
        user_id: '',
        merchant_id: '',
        refresh_token: '',
        promoter_id: '',
        promoter_name: '',
        auth_code: '',
        public_key: '',
        private_key: '',
        accessToken: '',
        authCode: '',
        access_token: '',
        default_provider: 'default',
        create_form_by: 'rate'
    };

    // required fields for each type of distributor
    const formValidation = {
        digitick: ['login', 'password', 'wsdl'],
        fever: ['api_key', 'user_id'],
        fnac: ['login', 'password'],
        universe: ['authCode'],
        lennd: ['accessToken'],
        ticketnet: ['login', 'password', 'account_type'],
        playpass: ['login', 'password', 'tenant', 'getin_url'],
        secutix: ['login', 'password', 'url'],
        seetickets_de: ['merchant_id', 'refresh_token'],
        seetickets_es: ['promoter_id', 'promoter_name'],
        regiondo: ['public_key', 'private_key'],
        vivenu: ['access_token']
    };

    const validateField = React.useCallback((name, value, formState) => {
        let fieldToValidate = formValidation[formState.type] || [];

        if (name === 'name' && formState.type !== 'hook') {
            return true;
        }
        if (fieldToValidate.includes(name)) {
            return value ? Boolean(value) : 'ticket.common.required';
        }
        return true;
    }, []);

    const requiredFields = React.useMemo(() => {
        return Object.keys(initialValues)
            .filter(item => !['type', 'end_date', 'channel_id', 'organizer_id'].includes(item))
            .reduce((acc, key) => ({ ...acc, [key]: validateField }), {});
    }, [validateField]);

    const [formState, setFormState, setFormStateObject, formErrors, formValidate, setFormErrors] = useForm(initialValues, { ...requiredFields });

    const handleSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return;
        }
        setIsSubmitting(true);
        let newSubscriptionModel = new SubscriptionModel();

        newSubscriptionModel.type = formState.type;
        if (formState.type !== 'hook') {
            delete formState.default_provider;
            newSubscriptionModel.setInfo(formState);
        } else {
            newSubscriptionModel.name = formState.name;
            newSubscriptionModel.end_date = formState.end_date;
            newSubscriptionModel.default_provider = formState.default_provider;
            newSubscriptionModel.create_form_by = formState.create_form_by;
        }

        newSubscriptionModel
            .save({ organizationId })
            .then(({ response }) => {
                toast.success(t('ticket.webservice.creationSuccess'));
                if (response.type === 'hook') {
                    history.push(`/ticket/O${organizationId}/webservices/${response.id}/attendees`);
                } else {
                    history.push(`/ticket/O${organizationId}/webservices`);
                }
            })
            .catch(error => {
                let message = t([`ticket.webservice.statuses.${error.code}`, 'ticket.webservice.creationError']);
                if (error.response.includes('This subscription already exists and is running')) {
                    message = t('ticket.webservice.creationErrorSameLogin');
                }
                toast.error(message);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [organizationId, formValidate, formState, toast]);

    return (
        <>
            <WeezHeader title={t('ticket.webservice.create.title')} backLink={{ onClick: () => history.push(`/ticket/O${organizationId}/webservices`) }} />
            <Card.FormManager>
                <Card.FormStep
                    title={t('ticket.webservice.create.type-title')}
                    subtitle={t('ticket.webservice.create.type-subtitle')}
                    editLabel={t('ticket.common.action.edit')}
                    summary={
                        <>
                            <span>{t('ticket.webservice.create.summary')}</span>
                            <span className={Styles['summary']}>
                                {formState.type === 'hook' ? t(`ticket.webservice.create.option-custom-import`) : t(`ticket.webservice.create.option-ticket-provider`)}
                            </span>
                        </>
                    }
                    rightComponent={
                        <Message type="tips" title={t('notifications.tips_title')} mini>
                            <Trans i18nKey="ticket.webservice.create.tips-message"></Trans>
                        </Message>
                    }
                >
                    <TypeComponent formState={formState} setFormState={setFormState} disableHook={!permissions.hasCapacity('WendyCapacity.WEBSERVICES_PUSH', organizationId)} />
                </Card.FormStep>
                <Card.FormStep
                    title={t('ticket.webservice.create.information-title')}
                    subtitle={
                        formState.type === 'hook'
                            ? t(`ticket.webservice.create.information-subtitle-custom-import`)
                            : t(`ticket.webservice.create.information-subtitle-ticket-provider`)
                    }
                    rightComponent={
                        formState.type === 'hook' ? (
                            <Message type="tips" title={t('notifications.tips_title')} mini>
                                <Trans i18nKey="ticket.webservice.create.import.tips-message"></Trans>
                            </Message>
                        ) : (
                            <div></div>
                        )
                    }
                >
                    {formState.type !== 'hook' ? (
                        <SelectDistributors
                            formState={formState}
                            setFormState={setFormState}
                            setFormStateObject={setFormStateObject}
                            formErrors={formErrors}
                            onSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            formValidation={formValidation}
                            setFormErrors={setFormErrors}
                            initialValues={initialValues}
                        />
                    ) : (
                        <CreateImport
                            formState={formState}
                            setFormState={setFormState}
                            timezone={organization.timezone}
                            onSubmit={handleSubmit}
                            isSubmitting={isSubmitting}
                            formErrors={formErrors}
                        />
                    )}
                </Card.FormStep>
            </Card.FormManager>
        </>
    );
}

CreateWebservice.propTypes = {
    organizationId: PropTypes.string.isRequired
};
